// src/pages/BankDetailsPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSave, FiCreditCard, FiInfo, FiAlertCircle, FiCheckCircle, FiShield } from 'react-icons/fi';
import { BankService } from '../services/bank.service';
import type { Bank, BankDetails } from '../services/bank.service';
import { MfaService } from '../services/mfa.service';
import { useNotification } from '../context/NotificationContext';
import styles from './BankDetailsPage.module.css';

const onlyDigits = (v: string) => (v || '').replace(/\D+/g, '');
const isValidClabe = (clabe: string) => onlyDigits(clabe).length === 18;

type BankDetailsFormProps = {
  embedded?: boolean;
  showIconInTitle?: boolean;
};

const BankDetailsForm: React.FC<BankDetailsFormProps> = ({ embedded = false, showIconInTitle = true }) => {
  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const safeT = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const [form, setForm] = useState<BankDetails>({
    holder_name: '',
    bank_name: '',
    clabe: '',
    account_number: '',
    rfc: '',
    notes: ''
  });

  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [hasExistingAccount, setHasExistingAccount] = useState(false);
  const canConfirmMfa = otpCode.trim().length >= 6;

  const canSave = useMemo(() => {
    return form.holder_name.trim().length > 2 && form.bank_name.trim().length > 1 && isValidClabe(form.clabe);
  }, [form]);

  const bankNames = useMemo(() => banks.map(bank => bank.name), [banks]);
  const showCustomBankOption = useMemo(
    () => !!form.bank_name && !bankNames.includes(form.bank_name),
    [form.bank_name, bankNames]
  );

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const [res, banksData] = await Promise.all([
          BankService.getMyBankDetails(),
          BankService.getBanks().catch(() => [] as Bank[])
        ]);
        if (!alive) return;

        setBanks(banksData);

        if (res) {
          setForm({
            holder_name: res.holder_name || '',
            bank_name: res.bank_name || '',
            clabe: res.clabe || '',
            account_number: res.account_number || '',
            rfc: res.rfc || '',
            notes: res.notes || ''
          });
          setHasExistingAccount(true);
        } else {
          setHasExistingAccount(false);
        }
      } catch {
        if (!alive) return;
        setError(safeT('bank.load_error', 'No se pudieron cargar tus datos bancarios.'));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, []);

  const onChange = (key: keyof BankDetails, value: string) => {
    setSuccess(null);
    setError(null);
    setMfaError(null);

    if (key === 'clabe') value = onlyDigits(value).slice(0, 18);
    if (key === 'account_number') value = onlyDigits(value).slice(0, 20);

    setForm(prev => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);

    if (!canSave) {
      const msg = safeT('bank.invalid_form', 'Revisa los campos requeridos.');
      setError(msg);
      addNotification(msg, 'error');
      return;
    }

    if (!isValidClabe(form.clabe)) {
      const msg = safeT('bank.invalid_clabe', 'La CLABE debe tener 18 dígitos.');
      setError(msg);
      addNotification(msg, 'error');
      return;
    }

    setSaving(true);
    try {
      const purpose = hasExistingAccount ? 'BANK_ACCOUNT_UPDATE' : 'BANK_ACCOUNT_CREATE';
      await MfaService.sendCode(purpose);
      const infoMsg = safeT('bank.mfa_code_sent', 'Enviamos un código de verificación a tu correo.');
      addNotification(infoMsg, 'info');
      setShowMfaModal(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : safeT('bank.mfa_code_error', 'No se pudo enviar el código MFA.');
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmMfa = async () => {
    setMfaError(null);
    if (!canConfirmMfa) return;
    setSaving(true);
    try {
      const code = otpCode.trim();
      const purpose = hasExistingAccount ? 'BANK_ACCOUNT_UPDATE' : 'BANK_ACCOUNT_CREATE';
      const verify = await MfaService.verifyCode(code, purpose);
      if (!verify?.verified) {
        const msg = verify?.message || safeT('bank.mfa_code_error', 'Código MFA inválido o expirado.');
        setMfaError(msg);
        addNotification(msg, 'error');
        return;
      }

      await BankService.updateMyBankDetails({
        ...form,
        clabe: onlyDigits(form.clabe)
      }, code);
      setHasExistingAccount(true);
      const okMsg = safeT('bank.saved_ok', 'Datos bancarios guardados correctamente.');
      setSuccess(okMsg);
      addNotification(okMsg, 'success');
      setShowMfaModal(false);
      setOtpCode('');
    } catch (e) {
      const msg = e instanceof Error ? e.message : safeT('bank.saved_error', 'No se pudieron guardar tus datos bancarios.');
      setMfaError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={embedded ? undefined : styles.pageContainer}>
      {!embedded ? (
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            <FiCreditCard /> {safeT('bank.my_title', 'Datos bancarios')}
          </h1>
          <p className={styles.subTitle}>
            {safeT('bank.my_subtitle', 'Estos datos se usarán para retiros, devoluciones o transferencias (si aplica).')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          {showIconInTitle ? <FiCreditCard /> : null}
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>
            {safeT('bank.my_title', 'Datos bancarios')}
          </h2>
        </div>
      )}

      <div className={styles.surface}>
        <div className={styles.contentArea}>
          {error ? (
            <div className={`${styles.alert} ${styles.alertError}`}>
              <FiAlertCircle />
              <div>{error}</div>
            </div>
          ) : null}

          {mfaError ? (
            <div className={`${styles.alert} ${styles.alertError}`}>
              <FiAlertCircle />
              <div>{mfaError}</div>
            </div>
          ) : null}

          {success ? (
            <div className={`${styles.alert} ${styles.alertSuccess}`}>
              <FiCheckCircle />
              <div>{success}</div>
            </div>
          ) : null}

          {loading ? (
            <div>{safeT('common.loading', 'Cargando...')}</div>
          ) : (
            <form onSubmit={onSubmit}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>{safeT('bank.holder', 'Titular')} *</label>
                  <input
                    className={styles.field}
                    value={form.holder_name}
                    onChange={e => onChange('holder_name', e.target.value)}
                    placeholder={safeT('bank.holder_placeholder', 'Nombre completo del titular')}
                    disabled={saving}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>{safeT('bank.bank', 'Banco')} *</label>
                  <select
                    className={styles.field}
                    value={form.bank_name || ''}
                    onChange={e => onChange('bank_name', e.target.value)}
                    disabled={saving}
                    required
                  >
                    {showCustomBankOption ? (
                      <option value={form.bank_name}>{form.bank_name}</option>
                    ) : null}
                    <option value="">
                      {safeT('bank.bank_select_placeholder', 'Selecciona un banco')}
                    </option>
                    {banks.map(bank => (
                      <option key={bank.id} value={bank.name}>
                        {bank.name} {bank.code ? `(${bank.code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>{safeT('bank.clabe', 'CLABE')} *</label>
                  <input
                    className={styles.field}
                    value={form.clabe}
                    onChange={e => onChange('clabe', e.target.value)}
                    placeholder={safeT('bank.clabe_placeholder', '18 dígitos')}
                    inputMode="numeric"
                    disabled={saving}
                    required
                  />
                  <div className={styles.helperText}>
                    {safeT('bank.clabe_hint', 'La CLABE debe tener exactamente 18 dígitos.')}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    {safeT('bank.account_number', 'Número de cuenta')} ({safeT('bank.optional', 'opcional')})
                  </label>
                  <input
                    className={styles.field}
                    value={form.account_number || ''}
                    onChange={e => onChange('account_number', e.target.value)}
                    placeholder={safeT('bank.account_number_placeholder', 'Opcional')}
                    inputMode="numeric"
                    disabled={saving}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    {safeT('bank.rfc', 'RFC')} ({safeT('bank.optional', 'opcional')})
                  </label>
                  <input
                    className={styles.field}
                    value={form.rfc || ''}
                    onChange={e => onChange('rfc', e.target.value)}
                    placeholder={safeT('bank.rfc_placeholder', 'Opcional')}
                    disabled={saving}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    {safeT('bank.notes', 'Notas')} ({safeT('bank.optional', 'opcional')})
                  </label>
                  <input
                    className={styles.field}
                    value={form.notes || ''}
                    onChange={e => onChange('notes', e.target.value)}
                    placeholder={safeT('bank.notes_placeholder', 'Ej. Cuenta principal')}
                    disabled={saving}
                  />
                </div>
              </div>

              <div className={styles.infoRow}>
                <FiInfo />
                <div>
                  {safeT('bank.security_hint', 'Recomendación: asegúrate de que la CLABE esté correcta antes de guardar.')}
                </div>
              </div>

              <button type="submit" disabled={saving || !canSave} className={styles.saveButton}>
                <FiSave />
                {saving ? safeT('common.saving', 'Guardando...') : safeT('common.save_changes', 'Guardar Cambios')}
              </button>
            </form>
          )}
        </div>
      </div>

      {showMfaModal && (
        <div
          role="dialog"
          aria-modal="true"
          onMouseDown={e => {
            if (e.target === e.currentTarget && !saving) setShowMfaModal(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 9999,
            padding: 16
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 440,
              background: '#fff',
              borderRadius: 18,
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 18px 40px rgba(0,0,0,0.20)',
              padding: 18,
              display: 'grid',
              gap: 12,
              color: 'var(--color-texto-principal)'
            }}
          >
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{
                  width: 42,
                  height: 42,
                  borderRadius: 14,
                  background: 'rgba(103,58,183,0.12)',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'var(--color-primario-oscuro)'
                }}>
                  <FiShield size={20} />
                </div>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 18 }}>Verificación MFA</div>
                  <div style={{ color: 'var(--color-texto-secundario)', fontWeight: 700, fontSize: 13 }}>
                    Ingresa el código de 6 dígitos enviado a tu correo.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !saving && setShowMfaModal(false)}
                style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: 'var(--color-texto-secundario)' }}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontWeight: 800 }}>Código MFA</span>
              <input
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\s/g, ''))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                disabled={saving}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 12,
                  fontWeight: 900,
                  letterSpacing: 2,
                  outline: 'none'
                }}
              />
            </label>

            {mfaError ? (
              <div style={{ color: '#b91c1c', fontWeight: 800, fontSize: 13 }}>{mfaError}</div>
            ) : null}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={async () => {
                  setMfaError(null);
                  setSaving(true);
                  try {
                    const purpose = hasExistingAccount ? 'BANK_ACCOUNT_UPDATE' : 'BANK_ACCOUNT_CREATE';
                    await MfaService.sendCode(purpose);
                    const msg = safeT('bank.mfa_code_sent', 'Enviamos un código de verificación a tu correo.');
                    addNotification(msg, 'info');
                  } catch (error) {
                    const msg = error instanceof Error ? error.message : safeT('bank.mfa_code_error', 'No se pudo enviar el código MFA.');
                    setMfaError(msg);
                    addNotification(msg, 'error');
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  padding: '10px 12px',
                  background: '#fff',
                  fontWeight: 800,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  color: 'var(--color-texto-principal)'
                }}
              >
                Reenviar código
              </button>

              <button
                type="button"
                onClick={confirmMfa}
                disabled={!canConfirmMfa || saving}
                style={{
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 14px',
                  background: 'var(--color-primario)',
                  color: '#fff',
                  fontWeight: 900,
                  cursor: !canConfirmMfa || saving ? 'not-allowed' : 'pointer',
                  boxShadow: '0 10px 25px rgba(103, 58, 183, 0.35)'
                }}
              >
                {saving ? 'Validando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const BankDetailsSection: React.FC = () => {
  return <BankDetailsForm embedded />;
};

const BankDetailsPage: React.FC = () => {
  return <BankDetailsForm embedded={false} />;
};

export default BankDetailsPage;
